import { Avatar, Badge, Typography } from '@cannect/new-components/atoms'
import { Button } from '@cannect/new-components/atoms/Button'
import { PrescriberDetailCard } from '@cannect/new-components/organisms'
import { TSchedulingPrescriberListItem, useGetAvailablePrescribers } from '@cannect/services/resources/scheduling'
import { formatToReal } from '@cannect/utils/number'
import { getLettersForName } from '@cannect/utils/user'
import { Skeleton } from 'antd'
import { useBrand } from 'hooks/useBrand'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { ASPAEC_PRESCRIBER_IDS } from '../../constants'

type FilterByStepBrandedProps = {
  onBack: () => void
}

export default function FilterByStepBranded({ onBack }: FilterByStepBrandedProps) {
  const { config } = useBrand()
  const { originId, priceField } = config.scheduling
  const [selectedPrescriberId, setSelectedPrescriberId] = useState<number | null>(null)

  const currentFilterBy =
    config.key === 'aspaec' && originId == null
      ? { type: 'prescribers_ids' as const, value: JSON.stringify(ASPAEC_PRESCRIBER_IDS) }
      : { type: 'origin_scheduling' as const, value: String(originId) }

  const { data: availablePrescribers, isFetching: loadingPrescribers } = useGetAvailablePrescribers({
    filterBy: currentFilterBy,
    sortBy: 'available_date_times',
    orderBy: 'asc'
  })

  const prescribersList = availablePrescribers?.prescribers || []

  // Preço da marca (association p/ Aspaec, remederi p/ Remederi/Vikura);
  // médico sem o valor da marca preenchido cai no preço online padrão.
  const getPriceOverride = (prescriber: TSchedulingPrescriberListItem) =>
    priceField !== 'online_appointment_value' ? (prescriber[priceField] ?? null) : null

  const selectedPrescriber = prescribersList.find((prescriber) => prescriber.id === selectedPrescriberId) ?? null

  const handleSelectPrescriber = (prescriberId: number | null) => {
    setSelectedPrescriberId(prescriberId)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="container mx-auto w-full max-w-[1164px] px-4 py-8">
      {!selectedPrescriber && (
        <Button
          unstyled
          onClick={onBack}
          icon={<ArrowLeft className="size-4" />}
          iconPlacement="left"
          className="mb-4 gap-2 font-semibold text-neutral-700">
          Voltar
        </Button>
      )}

      <div className="mb-8 text-center">
        <Typography.Heading type="headingTwo" className="text-primary">
          Escolha seu especialista
        </Typography.Heading>
        <Typography.Text type="paragraphOne" muted className="mt-2 block">
          {selectedPrescriber
            ? 'Escolha o melhor horário para a sua consulta.'
            : 'Selecione o profissional para ver os horários disponíveis.'}
        </Typography.Text>
      </div>

      {loadingPrescribers ? (
        <Skeleton active paragraph={{ rows: 4 }} round />
      ) : selectedPrescriber ? (
        <div className="space-y-4">
          <Button
            unstyled
            onClick={() => handleSelectPrescriber(null)}
            icon={<ArrowLeft className="size-4" />}
            iconPlacement="left"
            className="gap-2 font-semibold text-neutral-700">
            Voltar para a lista de médicos
          </Button>
          <div className="rounded-2xl bg-white/95 p-1 shadow-lg ring-1 ring-black/5">
            <PrescriberDetailCard
              prescriberInfo={selectedPrescriber}
              priceOverride={getPriceOverride(selectedPrescriber)}
            />
          </div>
        </div>
      ) : prescribersList.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {prescribersList.map((prescriber) => {
            const expertises = (prescriber.prescriber_expertises || []).map((expertise) => expertise.name)
            const displayedPrice = getPriceOverride(prescriber) ?? prescriber.online_appointment_value

            return (
              <button
                key={prescriber.id}
                type="button"
                onClick={() => handleSelectPrescriber(prescriber.id)}
                className="flex cursor-pointer flex-col items-center gap-3 rounded-2xl border-0 bg-white/95 p-6 text-center shadow-lg ring-1 ring-black/5 transition-shadow hover:shadow-xl">
                <Avatar.Root className="size-20">
                  <Avatar.Image src={prescriber.photo} alt={prescriber.professional_name} />
                  <Avatar.Fallback>{getLettersForName(prescriber.professional_name)}</Avatar.Fallback>
                </Avatar.Root>

                <div className="flex flex-col items-center gap-1">
                  <Typography.Text type="paragraphTwo" weight="semibold" className="line-clamp-2">
                    {prescriber.professional_name}
                  </Typography.Text>
                  <Typography.Text type="captionOne" muted>
                    {prescriber.council}: {prescriber.nr_council} {prescriber.uf_council}
                  </Typography.Text>
                  {expertises.length > 0 && (
                    <Typography.Text type="captionOne" muted className="line-clamp-1">
                      {expertises.join(' • ')}
                    </Typography.Text>
                  )}
                </div>

                <Badge className="bg-primary-700 font-semibold text-white">
                  {formatToReal(displayedPrice)} no pix
                </Badge>

                <span className="mt-auto w-full rounded-lg bg-primary-700 px-4 py-2 text-sm font-semibold text-white">
                  Ver horários
                </span>
              </button>
            )
          })}
        </div>
      ) : (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-neutral-200 bg-white/95">
          <Typography.Text type="paragraphOne" muted>
            Nenhum profissional encontrado.
          </Typography.Text>
        </div>
      )}
    </div>
  )
}
